import { endpoints } from 'config';
import { get, post } from '../../../helpers/newMakeEndpointRequest';
import {
  ClassificationTestDetails,
  ClassificationTestMaterials,
  ParsedClassificationTestDetails,
  ParsedClassificationTestMaterials,
} from './models';
import instance from 'api';

type ApiReturn<T = any> = {
  response: T | null;
  errors: any;
};

export const getClassificationTestDetails = async (params: {
  assessmentProngID: number;
}): Promise<any> => {
  const url = endpoints.classificationTest.getClassificationTestDetails;
  return await get(url, params);
};

export const parseGetClassificationTestDetails = (
  details: ClassificationTestDetails
): ParsedClassificationTestDetails => {
  const {
    assessmentProngID,
    completedTestPages,
    firstName,
    instructionBody,
    instructionRightPanel,
    overriddenProngDisplayName,
    practiceTestPages,
    startTime,
    testPopUp,
    testTime,
    uncompletedTestPages,
    errors,
    id,
    status,
  } = details;
  return {
    assessmentProngID,
    completedTestPages,
    firstName,
    instructionBody,
    instructionRightPanel,
    overriddenProngDisplayName,
    practiceTestPages,
    startTime,
    testPopUp,
    testTime,
    uncompletedTestPages,
    errors,
    id,
    status,
  };
};

export const startClassificationTest = async (payload: any) => {
  try {
    const res = await instance.post(
      endpoints.classificationTest.startTest,
      payload
    );
    if (res.data.status === '1') {
      return {
        response: res.data,
        errors: null,
      };
    } else {
      return {
        response: null,
        errors: res.data.errors,
      };
    }
  } catch (res) {
    const error: any = res;

    if (error.response && error.response.data && error.response.data.errors) {
      return {
        response: null,
        errors: error.response.data.errors,
      };
    } else {
      return {
        response: null,
        errors: [],
      };
    }
  }
};

export const completeClassificationTest = async (params: {
  assessmentProngID: number;
}): Promise<ApiReturn> => {
  const url = endpoints.classificationTest.completeTest;
  return await post(url, params);
};

export const parseClassificationTestMaterials = (
  classificationTestMaterials: ClassificationTestMaterials[]
): ParsedClassificationTestMaterials[] =>
  Array.isArray(classificationTestMaterials)
    ? classificationTestMaterials.map(
        ({
          materialsDocument,
          materialsDocumentName,
          materialsDocumentExtension,
        }) => ({
          name: `${materialsDocumentName}${materialsDocumentExtension}`,
          link: materialsDocument,
        })
      )
    : [];

export const parseSaveClassificationTestAnswer = (
  assessmentProngID: number,
  classificationTestQuestionID: number,
  answer: any
) => {
  let parsedAnswer = answer;

  return {
    assessmentProngID,
    classificationTestQuestionID,
    answers: parsedAnswer,
  };
};

export const saveClassificationTestAnswer = async (
  payload: any
): Promise<ApiReturn> => {
  const url = endpoints.classificationTest.setAnswers;
  return await post(url, payload);
};

// Get list of reasons for requesting a reasonable adjustment
export const getReasonableAdjustmentReasons = async () => {
  try {
    const res = await instance.get(
      endpoints.classificationTest.getResonableAdjustmentReasons
    );
    if (res.data.status === '1') {
      return {
        response: res.data,
        errors: null,
      };
    } else {
      return {
        response: null,
        errors: res.data.errors,
      };
    }
  } catch (res) {
    const error: any = res;

    if (error.response && error.response.data && error.response.data.errors) {
      return {
        response: null,
        errors: error.response.data.errors,
      };
    } else {
      return {
        response: null,
        errors: [],
      };
    }
  }
};

// Submit a request for a reasonable adjustment
export const requestReasonableAdjustment = async (payload: any) => {
  try {
    const res = await instance.post(
      endpoints.classificationTest.requestResonableAdjustment,
      payload
    );
    if (res.data.status === '1') {
      return {
        response: res.data,
        errors: null,
      };
    } else {
      return {
        response: null,
        errors: res.data.errors,
      };
    }
  } catch (res) {
    const error: any = res;

    if (error.response && error.response.data && error.response.data.errors) {
      return {
        response: null,
        errors: error.response.data.errors,
      };
    } else {
      return {
        response: null,
        errors: [],
      };
    }
  }
};
